import React, { useEffect, useMemo, useState } from "react";
import { XCircle, Download, ExternalLink, FileText, X, ChevronLeft, ChevronRight } from "lucide-react";
import "../styles/DocumentViewer.css";

export const DocumentViewer = ({ file, onClose, onPrev, onNext }) => {
    const [fileContent, setFileContent] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const fileType = file?.name?.split(".").pop().toLowerCase();

    const openFileInNewTab = async () => {
        const directOpenFallbackAllowed = file?.allowDirectOpenFallback !== false;
        const explicitOpenUrl = file?.open_url || file?.openUrl || '';
        if (explicitOpenUrl) {
            window.open(explicitOpenUrl, "_blank", "noopener,noreferrer");
            return;
        }

        try {
            const response = await fetch(file.url, { credentials: 'include' });
            if (!response.ok) {
                throw new Error(`Open failed with HTTP ${response.status}`);
            }
            const blob = await response.blob();
            const objectUrl = window.URL.createObjectURL(blob);
            const opened = window.open(objectUrl, "_blank", "noopener,noreferrer");
            if (opened) {
                setTimeout(() => window.URL.revokeObjectURL(objectUrl), 120000);
                return;
            }
            window.URL.revokeObjectURL(objectUrl);
            throw new Error('Popup was blocked');
        } catch (error) {
            console.error('Unable to open shared file without exposing the signed URL:', error);
            if (directOpenFallbackAllowed) {
                window.open(file.url, "_blank", "noopener,noreferrer");
            } else {
                alert('Unable to open this file in a new tab. Please use Download instead.');
            }
        }
    };

    const getDownloadName = () => {
        if (file?.name) return file.name;
        if (file?.url) {
            try {
                const url = new URL(file.url);
                const pathname = decodeURIComponent(url.pathname || "");
                const last = pathname.split("/").filter(Boolean).pop();
                if (last) return last;
            } catch {
                // ignore
            }
        }
        return "download";
    };

    const readableSize = useMemo(() => {
        if (!file?.size) return null;
        const size = Number(file.size);
        if (Number.isNaN(size)) return null;
        if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
        return `${(size / 1024 / 1024).toFixed(1)} MB`;
    }, [file?.size]);

    useEffect(() => {
        setLoadError(false);
        if (["txt", "csv", "json", "log", "md", "eml"].includes(fileType)) {
            fetch(file.url, { credentials: 'include' })
                .then((res) => {
                    if (!res.ok) {
                        throw new Error(`Preview failed with HTTP ${res.status}`);
                    }
                    return res.text();
                })
                .then((text) => setFileContent(text))
                .catch(() => {
                    setLoadError(true);
                    setFileContent("⚠ Unable to load content.");
                });
        } else {
            setFileContent(file.url);
        }
    }, [file.url, fileType]);

    useEffect(() => {
        const handleKey = (event) => {
            if (event.defaultPrevented) return;
            const target = event.target;
            const tag = target?.tagName?.toLowerCase();
            if (tag === "input" || tag === "textarea" || target?.isContentEditable) return;

            if (event.key === "ArrowLeft") {
                onPrev?.();
            } else if (event.key === "ArrowRight") {
                onNext?.();
            } else if (event.key === "Escape") {
                onClose?.();
            }
        };

        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [onPrev, onNext, onClose]);

    if (!file || !file.url) return null;

    let content;
    if (["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(fileType)) {
        content = <img src={fileContent} alt={file.name} className="image-preview" />;
    } else if (["txt", "log", "md", "eml"].includes(fileType)) {
        content = <pre className="text-preview">{fileContent}</pre>;
    } else if (fileType === "json") {
        let pretty = fileContent;
        try {
            pretty = JSON.stringify(JSON.parse(fileContent || ""), null, 2);
        } catch {
            // keep raw
        }
        content = <pre className="text-preview">{pretty}</pre>;
    } else if (fileType === "csv") {
        const rows = (fileContent || "").split(/\r?\n/).filter(Boolean).slice(0, 200);
        const table = rows.map((line) => line.split(","));
        content = (
            <div className="table-preview">
                <table>
                    <tbody>
                        {table.map((row, idx) => (
                            <tr key={idx}>
                                {row.map((cell, cIdx) => (
                                    <td key={cIdx}>{cell}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    } else if (fileType === "pdf") {
        content = <iframe src={fileContent} className="pdf-viewer" title={file.name} />;
    } else if (["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(fileType)) {
        content = (
            <iframe
                src={`https://docs.google.com/gview?url=${encodeURIComponent(file.url)}&embedded=true`}
                className="office-viewer"
                title="Office Document Viewer"
            />
        );
    } else if (["mp3", "wav", "ogg", "aac", "flac"].includes(fileType)) {
        content = (
            <audio controls className="media-player">
                <source src={file.url} />
            </audio>
        );
    } else if (["mp4", "webm", "mov"].includes(fileType)) {
        content = (
            <video controls className="media-player">
                <source src={file.url} />
            </video>
        );
    } else {
        content = (
            <div className="unsupported-file">
                <FileText size={32} />
                <p>Preview not available for this file type.</p>
            </div>
        );
    }

    return (
        <div className="document-viewer-overlay" onClick={onClose}>
            <div className="document-viewer" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                <div className="document-viewer-header">
                    <div className="document-viewer-title">
                        <span className="document-viewer-name">{file?.name}</span>
                        <span className="document-viewer-meta">
                            {fileType?.toUpperCase() || "FILE"}
                            {readableSize ? ` • ${readableSize}` : ""}
                            {file?.last_modified ? ` • ${new Date(file.last_modified).toLocaleString()}` : ""}
                        </span>
                    </div>
                    <div className="document-viewer-actions">
                        {(onPrev || onNext) && (
                            <div className="viewer-nav">
                                <button
                                    className="viewer-action"
                                    onClick={onPrev}
                                    title="Previous file"
                                    disabled={!onPrev}
                                >
                                    <ChevronLeft size={16} />
                                </button>
                                <button
                                    className="viewer-action"
                                    onClick={onNext}
                                    title="Next file"
                                    disabled={!onNext}
                                >
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        )}
                        <button
                            className="viewer-action"
                            onClick={openFileInNewTab}
                            title="Open in new tab"
                        >
                            <ExternalLink size={16} />
                            Open
                        </button>
                        <button
                            className="viewer-action"
                            onClick={() => {
                                const downloadName = getDownloadName();
                                const explicitDownloadUrl = file?.download_url || file?.downloadUrl || '';
                                if (explicitDownloadUrl) {
                                    const link = document.createElement("a");
                                    link.href = explicitDownloadUrl;
                                    link.download = downloadName;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    return;
                                }
                                fetch(file.url, { credentials: 'include' })
                                    .then((res) => {
                                        if (!res.ok) {
                                            throw new Error(`Download failed with HTTP ${res.status}`);
                                        }
                                        return res.blob();
                                    })
                                    .then((blob) => {
                                        const url = window.URL.createObjectURL(blob);
                                        const link = document.createElement("a");
                                        link.href = url;
                                        link.download = downloadName;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                        window.URL.revokeObjectURL(url);
                                    })
                                    .catch(() => {
                                        if (file?.allowDirectOpenFallback !== false) {
                                            window.open(file.url, "_blank", "noopener,noreferrer");
                                        } else {
                                            alert('Unable to download this file from the shared viewer. Please try again.');
                                        }
                                    });
                            }}
                            title="Download"
                        >
                            <Download size={16} />
                            Download
                        </button>
                        <button className="viewer-action close" onClick={onClose}>
                            <X size={16} />
                        </button>
                    </div>
                </div>
                <div className="document-viewer-body">
                    {loadError ? (
                        <div className="unsupported-file">
                            <FileText size={32} />
                            <p>Unable to load preview.</p>
                        </div>
                    ) : (
                        content
                    )}
                </div>
            </div>
        </div>
    );
};
